/* =======================================================================
   Global Variables and Element References
======================================================================== */

/**
 * Global counter to assign unique IDs to the "Show more" buttons.
 */
let showMoreBtnCounter = 1;

/**
 * Stores the discount brackets extracted from the discount table.
 */
let discountBrackets = [];

/**
 * Variable to track whether to include VAT
 */
let includeVAT = true;

/**
 * Retrieve the current market VAT rate.
 * If the input is "21", it will be converted to 0.21 by dividing by 100.
 * If the input is "21.5", it becomes 0.215, which is 21.5%.
 */
const vatRateInput = document.getElementById('current-market-vat-rate');
let vatRate = 0;

if (vatRateInput) {
  const rawVATValue = parseFloat(vatRateInput.value);
  if (!isNaN(rawVATValue)) {
    // Convert from "21" or "21.5" to "0.21" or "0.215"
    vatRate = rawVATValue / 100;
  }
}

/** 
 * Get the Market ID from the hidden input on the page
 */
const marketId_element = document.getElementById('store-market-id');
const marketId = marketId_element.value;

/** 
 * Get the ISO code from the hidden input on the page
 */
const iso_code_element = document.getElementById('store-iso-code');
const iso_code = iso_code_element ? iso_code_element.value : 'en'; // Default to 'en' if not found

/** 
 * Get the Product ID from the hidden input on the page
 */
const productId = document.querySelector('input[name="product-id"]').value;

/** 
 * Global array to store excluded variant IDs
 */
let excludedVariantIds = [];

/**
 * Global currency symbol, detected from original prices
 */
let currencySymbol = '';

/**
 * Indicates whether the currency comes before or after the price. Values: ‘before’ | ‘after’
 */
let currencySymbolPlacement = 'after';

/**
 * Declare original single unit and compare prices
 */
let originalSingleUnitPriceWithVAT = 0;
let originalComparePriceWithVAT = 0;

/**
 * Flag indicating whether the discount percentage column exists.
 * Assuming it always exists in this implementation.
 */
let discountPercentageColumnExists = true;

/**
 * Declare original print system iframe url src empty
 */
let printSystemIframeUrlSrc = "";

/**
 * DOM Element references used throughout the script.
 */
const personaliseProductIframe = document.getElementById('personalise-product-iframe');
const printSystemIframeUrlInput = document.getElementById('print-system-iframe-url');
const optionSelectors = document.querySelectorAll('.option-selector');
const lbVdContainer = document.querySelector('.lb-vd-container');
const productMedia = document.getElementById('product-media');
const personaliseProductBtn = document.querySelector('#personalise-product-btn');
const quantityInput = document.querySelector('.qty-input__input');

/* =======================================================================
   Initialization Functions
======================================================================== */

/**
 * This code is executed once the DOM content has fully loaded. It ensures that
 * various initialization tasks are performed, including setting up prices, 
 * custom tabs, variant change handling, VAT toggle, and limiting variant options.
 */
document.addEventListener('DOMContentLoaded', async function() {
  
  // 1) Loader
  var loader = lottie.loadAnimation({
    container: document.getElementById('custom-spinner'),
    renderer: 'svg',
    loop: true,
    autoplay: true,
    path: 'https://lottie.host/9540268e-d431-4041-8ab0-fe5975383aac/migM314XHU.json'
  });

  // 2) Fetch excluded variants for that product in current market if product has more than 1 variant
  if(optionSelectors.length != 0){
    await fetchExcludedVariantIds();
  } 
  
  // 3) Initialize original product prices
  initializeOriginalPrices();
  
  // 4) Reorder measure-like fieldsets
  autoReorderMeasureFieldsets();

  // 5) Custom tabs
  initCustomTabs();

  // 6) Quantity input changes
  initQuantityInputChangeHandler();

  // 7) Toggle VAT inclusion
  initializeVATToggle();

  // 8) Limit variant options to show only 6, with a "Show more" button
  limitVariantOptions();

  // 9) Variant change handler
  initVariantChangeHandler();
  
  // 10) Hide the loader
  document.getElementById('custom-loading-spinner').style.display = 'none';
});

/**
 * Initialize the original prices (price_current and price_was) with VAT
 * so we can compute exVAT if needed.
 */
function initializeOriginalPrices() {
  const priceCurrentElement = document.querySelector('.price__current');
  if (priceCurrentElement) {
    const priceText = priceCurrentElement.textContent.trim();

    // Detect the currency symbol by removing digits, commas, periods, and spaces
    const symbolMatch = priceText.replace(/[0-9.,\s]/g, '');
    currencySymbol = symbolMatch || '';

    // Determine where the currency symbol should be placed
    currencySymbolPlacement = determineCurrencySymbolPlacement(currencySymbol);

    // Get the base numeric price value
    originalSingleUnitPriceWithVAT = parseUnitPrice(priceText);
  } else {
    // Default to 0 if the price element is not found
    originalSingleUnitPriceWithVAT = 0;
    currencySymbol = '';
  }

  // Get the "was" price and extract its value
  const priceWasElement = document.querySelector('.price__was');
  if (priceWasElement) {
    const wasText = priceWasElement.textContent.trim();
    originalComparePriceWithVAT = parseUnitPrice(wasText);
  } else {
    originalComparePriceWithVAT = 0;
  }
}

/**
 * Function to fetch excluded variant IDs of the product by current market
 */
async function fetchExcludedVariantIds() {
  try {
    const response = await fetch('https://api-shopify.enveseur.com/shopify-product/excluded-market-variants/product/' + productId + '/market/' + marketId);
    excludedVariantIds = await response.json();
  } catch (error) {
    console.error("Error fetching excluded variant IDs:", error);
  }
}

/* =======================================================================
   Variant Handling Functions
======================================================================== */

/**
 * Retrieves the selected variant based on the selected options.
 * @param {NodeList} optionSelectors - The list of option selector elements.
 * @returns {Object|null} The selected variant object or null if not found.
 */
function getSelectedVariant(optionSelectors) {
  const productJsonElement = document.querySelector('variant-picker script[type="application/json"]');

  if (productJsonElement) {
    let productData = null;
    try {
      productData = JSON.parse(productJsonElement.textContent);
    } catch (e) {
      return null;
    }

    const selectedOptions = Array.from(optionSelectors).map((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      return checkedInput ? checkedInput.value : null;
    });

    // If no options are selected, return the first available variant
    if (selectedOptions.includes(null)) {
      return productData.product.variants[0] || null;
    }

    // Find the variant that matches the selected options
    return productData.product.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }else {
    // No productJsonElement found, likely a single product without multiple variants.
    // Retrieve the variant SKU from the span '.product-variant_sku__value'
    // Retrieve the variant ID from the hidden input '#product-variant_id__value'
    const skuElement = document.querySelector('.product-sku__value');
    const singleVariantSkuValue = skuElement.textContent;
    const singleVariantIdInput = document.getElementById('product-variant_id__value');
    if (singleVariantSkuValue && singleVariantIdInput) {
      const variantSku = singleVariantSkuValue;
      const variantId = singleVariantIdInput.value;
      
      // Return a minimal variant object containing the SKU and the ID.
      // If additional properties are needed, they can be included here.
      return {
        sku: variantSku,
        id: variantId
      };
    }
  }
  
  return null;
}

// If you have a single "personalise" button:
const personaliseButton = document.querySelector('#personalise-product-btn');
if (personaliseButton) {
  personaliseButton.addEventListener('click', () => {
    // Each time the user clicks, we set the iframe src
    // to the latest lazyIframeUrl (updated by variant changes).
    personaliseProductIframe.src = printSystemIframeUrlSrc;

    // Then open the modal
    window.location.hash = '#personalise-product-modal';
  });
}

const closeBtn = document.querySelector('#personalise-product-modal .close');
if (closeBtn) {
  closeBtn.addEventListener('click', () => {
    // Change the hash to close the modal (this will remove the :target state)
    window.location.hash = '';
  });
}

/**
 * Updates the iframe source with the provided SKU.
 * @param {string} sku - The SKU of the selected variant.
 */
function updatepersonaliseProductIframeSrc(sku) {
  if(printSystemIframeUrlInput){
    printSystemIframeUrlSrc = printSystemIframeUrlInput.value + sku;
  }
}

/**
 * Clears the iframe source.
 */
function clearpersonaliseProductIframeSrc() {
  if(personaliseProductIframe){
    personaliseProductIframe.src = '';
  } 
}

/**
 * Updates UI elements when a valid variant is selected.
 */
function updateUIForValidVariant() {
  // Remove blur effects and enable the "personalise" button
  lbVdContainer?.classList.remove('blurred');
  productMedia?.classList.remove('blurred');
  personaliseProductBtn?.classList.remove('disabled');

  // Remove blur from the discount table
  const discountTable = document.querySelector('.lb-vd-table');
  discountTable?.classList.remove('blurred');

  // Remove blur from the product metafields containers
  const metafieldsContainers = document.querySelectorAll('.product-metafields-container');
  metafieldsContainers.forEach(container => container.classList.remove('blurred'));
}

/**
 * Updates UI elements when no valid variant is selected.
 */
function updateUIForInvalidVariant() {
  // Apply blur effects and disable the "personalise" button
  lbVdContainer?.classList.add('blurred');
  productMedia?.classList.add('blurred');
  personaliseProductBtn?.classList.add('disabled');

  // Blur the discount table
  const discountTable = document.querySelector('.lb-vd-table');
  discountTable?.classList.add('blurred');

  // Blur the product metafields containers
  const metafieldsContainers = document.querySelectorAll('.product-metafields-container');
  metafieldsContainers.forEach(container => container.classList.add('blurred'));

  // Clear discount brackets and remove any highlighting as the variant is invalid
  discountBrackets = [];
  highlightCurrentPriceBreak(null);
}

/**
 * Updates the price break highlight based on the current quantity.
 */
function updatePriceBreakHighlight() {
  initDiscountBracketsWhenTableIsReady(function() {
    if (quantityInput) {
      const quantity = parseInt(quantityInput.value);
      if (!isNaN(quantity)) {
        highlightCurrentPriceBreak(quantity);
      }
    }
  });
}

/**
 * Handles the variant change event.
 */
async function handleVariantChange() {
  // Retrieve the currently selected variant
  const variant = getSelectedVariant(optionSelectors);

  if (variant && variant.sku) {

    // If the variant is not available in the market, clear the iframe and update UI.
    if(optionSelectors.length != 0){
      if (excludedVariantIds.includes(String(variant.id))) {
        clearpersonaliseProductIframeSrc();
        updateUIForInvalidVariant();
  
        setTimeout(() => {
          // Disable the add-to-cart button and update its text.
          const addToCartButton = document.querySelector('.product-info__add-button button[name="add"]');
          if (addToCartButton) {
            addToCartButton.disabled = true;
            addToCartButton.textContent = 'Unavailable';
          }
          // Hide the element with class "price__default"
          const priceDefault = document.querySelector('.price__default');
          if (priceDefault) {
            priceDefault.setAttribute('hidden', '');
          }
          // Show the element with class "price__no-variant" by removing the hidden attribute.
          const priceNoVariant = document.querySelector('.price__no-variant');
          if (priceNoVariant) {
            priceNoVariant.removeAttribute('hidden');
          }
          // Add the class "blurred" to the discount table.
          const discountTable = document.querySelector('.lb-vd-table');
          if (discountTable) {
            discountTable.classList.add('blurred');
          }
        }, 200);
  
        return; // Stop further processing.
      }
    }
    
    // Update the iframe source to reflect the selected variant's SKU
    updatepersonaliseProductIframeSrc(variant.sku);

    // Update UI for a valid variant selection
    updateUIForValidVariant();

    // Reset the quantity input to 1 for the new variant
    if (quantityInput) {
      quantityInput.value = 1;
    }

    // Clear existing discount brackets to ensure fresh data for the new variant
    discountBrackets = [];

    // Use a short delay to ensure the DOM updates are complete before proceeding
    setTimeout(async () => {
      // Re-initialize original prices after switching to the new variant
      initializeOriginalPrices();

      // Wait until discount brackets are ready, then update table prices and the displayed price
      initDiscountBracketsWhenTableIsReady(async () => {
        updateDiscountTablePricesWithVAT();
        updatePrice(); // Recalculate prices after variant change
        hideProductDiscountTableDiscountColumn();
        
        // Highlight the discount bracket corresponding to quantity 1 (default quantity)
        if (quantityInput) {
          highlightCurrentPriceBreak(parseInt(quantityInput.value) || 1);
        }

        // Fetch metafields for the currently selected variant and display them in the containers
        if (variant.id) {
          // Fetch variant metafields if product has more than 1 variant
          if(optionSelectors.length != 0){
            const variantData = await fetchProductVariantMetafieldsApiCall(variant.id);
            if (variantData) {
              displayVariantMetafieldsForContainers(variantData);
            }
          }
        }
      });
    }, 100); // Adjust the delay if necessary to ensure all elements are ready
    
  } else {
    // If the variant is invalid (no SKU or not found), update UI to reflect an invalid selection
    clearpersonaliseProductIframeSrc();
    updateUIForInvalidVariant();
  }
}

/**
 * Initializes the variant change handler by adding event listeners.
 */
function initVariantChangeHandler() {
  // Add event listeners to variant inputs
  optionSelectors.forEach(function(fieldset) {
    const inputs = fieldset.querySelectorAll('input');
    inputs.forEach(function(input) {
      input.addEventListener('change', handleVariantChange);
    });
  });

  // Initialize on page load
  handleVariantChange();
}

/* =======================================================================
   Quantity Handling Functions
======================================================================== */

/**
 * Initializes the quantity input change handler.
 */
function initQuantityInputChangeHandler() {
  if (!quantityInput) {
    return;
  }

  // Initialize discount brackets when the table is ready
  initDiscountBracketsWhenTableIsReady(function() {
    const initialQuantity = parseInt(quantityInput.value);
    if (!isNaN(initialQuantity)) {
      highlightCurrentPriceBreak(initialQuantity);
    }
    hideProductDiscountTableDiscountColumn();
  });

  // Add event listener to quantity input
  quantityInput.addEventListener('input', function() {
    const quantity = parseInt(quantityInput.value);

    if (isNaN(quantity)) {
      highlightCurrentPriceBreak(null);
    } else {
      highlightCurrentPriceBreak(quantity);
    }

    // After highlighting the correct bracket row, update the main price
    updatePrice();
  });
}

/* =======================================================================
   Reorder Measure Options Functions
======================================================================== */

/**
 * Attempts to parse "width x height" from a string (e.g. "162 x 229 mm", "110 x 220 mm (DL)").
 * Returns { width, height, area } or null if parsing fails.
 */
function parseWidthHeight(text) {
  // e.g. capturing "123 x 456"
  const regex = /(\d+)\s*x\s*(\d+)/i; // case-insensitive
  const match = regex.exec(text);
  if (!match) {
    return null;
  }
  const widthNum = parseFloat(match[1]);
  const heightNum = parseFloat(match[2]);
  return {
    width: widthNum,
    height: heightNum,
    area: widthNum * heightNum
  };
}

/**
 * Reorders measure-like fieldsets automatically by checking if the radio inputs look like "width x height".
 *
 * 1. Loops over all fieldsets.option-selector
 * 2. For each one, gathers radio inputs and tries to parse width x height
 * 3. If >= half parse successfully, reorder them from smallest area to largest
 */
function autoReorderMeasureFieldsets() {
  const fieldsets = document.querySelectorAll('fieldset.option-selector:not(.quick-add-drawer fieldset.option-selector)');
  fieldsets.forEach((fieldset) => {
    const container = fieldset.querySelector('.option-selector__btns');
    if (!container) return;

    // Gather all radio inputs
    const radioInputs = container.querySelectorAll('input[type="radio"].opt-btn');
    if (!radioInputs.length) return;

    // Build an array of { input, label, parseResult }
    let parseCount = 0;
    const items = Array.from(radioInputs).map((input) => {
      const label = container.querySelector(`label[for="${input.id}"]`);
      const parseResult = parseWidthHeight(input.value); 
      if (parseResult && parseResult.area) {
        parseCount++;
      }
      return {
        input,
        label,
        parseResult, // might be null if not parseable
      };
    });

    // If not enough parse successfully, skip reordering
    const threshold = items.length / 2; 
    if (parseCount < threshold) {
      // e.g., fewer than half are parseable => we assume it's not a measure fieldset
      return;
    }

    // Otherwise, reorder by ascending area
    items.sort((a, b) => {
      const areaA = (a.parseResult && a.parseResult.area) || Infinity;
      const areaB = (b.parseResult && b.parseResult.area) || Infinity;
      return areaA - areaB;
    });

    // Re-append them
    items.forEach(({ input, label }) => {
      if (input) input.remove();
      if (label) label.remove();
      if (input) container.appendChild(input);
      if (label) container.appendChild(label);
    });
  });
}

/* =======================================================================
   Discount Table Functions
======================================================================== */

/**
 * Initializes discount brackets when the discount table is ready.
 * @param {Function} callback - Function to call after discount brackets are initialized.
 */
function initDiscountBracketsWhenTableIsReady(callback) {
  // Find the discount table
  const table = document.querySelector('.lb-vd-table');
  if (table) {
    // If the table is already available, initialize brackets immediately
    getDiscountBrackets();
    callback?.();
  } else {
    // Use a MutationObserver to detect when the table is added to the DOM
    const observer = new MutationObserver(() => {
      const table = document.querySelector('.lb-vd-table');
      if (table) {
        observer.disconnect(); // Stop observing once the table is found
        getDiscountBrackets();
        callback?.();
      }
    });

    // Observe changes in the DOM
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

/**
 * Retrieves the discount brackets from the discount table on the page.
 * Assumes fixed column positions:
 * - Column 1: Quantity
 * - Column 2: Discount Percentage
 * - Column 3: Price per Unit
 */
function getDiscountBrackets() {
  // Clear existing discount brackets
  discountBrackets = [];

  // Find the discount table in the DOM
  const table = document.querySelector('.lb-vd-table');
  if (!table) {
    return;
  }

  // Iterate through table rows to extract discount brackets
  const rows = table.querySelectorAll('tbody tr');
  rows.forEach((row) => {
    const cells = row.querySelectorAll('td');
    if (cells.length < 2) {
      return;
    }

    // Extract data from table cells
    const quantityRangeText = cells[0].textContent.trim();
    const unitPriceText = cells[1].querySelector('.money')?.textContent.trim() || "0";

    // Parse the extracted data into structured information
    const quantityRange = parseQuantityRange(quantityRangeText);
    const originalFinalPriceWithVAT = parseUnitPrice(unitPriceText);

    // Add the parsed bracket to the array
    discountBrackets.push({
      min: quantityRange.min,
      max: quantityRange.max,
      originalFinalPriceWithVAT: originalFinalPriceWithVAT,
      rowElement: row,
    });
  });

  // Ensure brackets are sorted by quantity range
  discountBrackets.sort((a, b) => a.min - b.min);
}

/**
 * Determines the applicable discount bracket based on the quantity.
 * @param {number} quantity - The current quantity selected.
 * @returns {Object|null} The applicable discount bracket or null if none applies.
 */
function getDiscountBracket(quantity) {
  return discountBrackets.find(bracket => {
    return quantity >= bracket.min && (quantity <= bracket.max || bracket.max === Infinity);
  }) || null;
}

/**
 * Highlights the current discount bracket row in the discount table based on the quantity.
 * @param {number|null} quantity - The current quantity selected.
 */
function highlightCurrentPriceBreak(quantity) {
  const tableRows = document.querySelectorAll('.lb-vd-table tbody tr');
  tableRows.forEach(row => {
    row.classList.remove('current-price-break');
  });

  if (quantity === null || quantity <= 0 || discountBrackets.length === 0) {
    return;
  }

  const currentBracket = getDiscountBracket(quantity);

  if (currentBracket) {
    currentBracket.rowElement.classList.add('current-price-break');
  }
}

/**
 * Parses the quantity range from a text string.
 * @param {string} rangeText - The text containing the quantity range (e.g., "5 - 10", "10000+").
 * @returns {Object} An object with 'min' and 'max' properties.
 */
function parseQuantityRange(rangeText) {
  let min = 0, max = Infinity;

  if (rangeText.includes('+')) {
    min = parseInt(rangeText.replace('+', '').trim());
  } else if (rangeText.includes('-')) {
    const [minText, maxText] = rangeText.split('-').map(s => s.trim());
    min = parseInt(minText);
    max = parseInt(maxText);
  } else {
    min = parseInt(rangeText.trim());
    max = min;
  }

  return { min, max };
}

/**
 * Hide discount column of product discounts table
 */
function hideProductDiscountTableDiscountColumn() {
  // Locate the discount table element in the DOM
  const table = document.querySelector('.lb-vd-table');
  if (!table) return; // Exit the function if the table is not found

  // Hide the second header cell (TH) in the table's thead
  const theadCells = table.querySelectorAll('thead th');
  if (theadCells.length >= 2) { // Ensure the table has at least two columns
    theadCells[1].style.display = 'none'; // Hide the second column header
  }

  // Hide the second cell (TD) in each row of the table's tbody
  const rows = table.querySelectorAll('tbody tr');
  rows.forEach(row => {
    const tds = row.querySelectorAll('td');
    if (tds.length >= 2) { // Ensure the row has at least two columns
      tds[1].style.display = 'none'; // Hide the second column cell
    }
  });
}

/* =======================================================================
   Custom Tabs Functions
======================================================================== */

/**
 * Initializes custom tab functionalities.
 */
function initCustomTabs() {
  const specButton = document.querySelector('.product-specifications-btn');
  if (specButton) {
    specButton.addEventListener('click', () => {
      const tab = document.querySelector('#tab-4');
      if (tab) {
        tab.click();
        smoothScrollTo(tab, 1500);
      }
    });
  }
}

/* =======================================================================
   Limit Variant Options Functions
======================================================================== */

/**
 * Limits the displayed variant options to a specific number and adds "Show more"/"Show less" functionality.
 * Does NOT require .product-variant__item wrappers. Instead, it treats each label as one variant item.
 */
function limitVariantOptions() {
  const VISIBLE_ITEM_COUNT = 10;

  // Retrieve localized strings from hidden inputs
  const showMoreInput = document.getElementById('product-option-selector-show-more-label');
  const showLessInput = document.getElementById('product-option-selector-show-less-label');
  const showMoreText = showMoreInput?.value || 'Show more';
  const showLessText = showLessInput?.value || 'Show less';

  // Instead of .product-variant__container, select each fieldset
  const fieldsets = document
  .querySelector('.product-main')
  ?.querySelectorAll('fieldset.option-selector');

  fieldsets.forEach(fieldset => {
    // Gather all <label class="opt-label"> inside .option-selector__btns
    const variantLabels = Array.from(
      fieldset.querySelectorAll('.option-selector__btns label.opt-label')
    );

    // Find which labels are "enabled" by checking the matching input
    const enabledVariantLabels = variantLabels.filter(label => {
      const inputId = label.getAttribute('for');
      if (!inputId) return false;

      const input = fieldset.querySelector(`#${inputId}`);
      if (!input) return false;

      const isItemDisabled = input.classList.contains('disabled');
      const isInputDisabled = input.disabled;
      return !isItemDisabled && !isInputDisabled;
    });

    if (enabledVariantLabels.length > VISIBLE_ITEM_COUNT) {
      let enabledIndex = 0;

      // Show/hide each label
      variantLabels.forEach(label => {
        if (enabledVariantLabels.includes(label)) {
          if (enabledIndex < VISIBLE_ITEM_COUNT) {
            label.classList.remove('custom-hidden');
          } else {
            label.classList.add('custom-hidden');
          }
          enabledIndex++;
        } else {
          // If "disabled," we usually keep it visible but unclickable
          label.classList.remove('custom-hidden');
        }
      });

      // Look up the .show-more-btn-container
      let showMoreBtnContainer = fieldset.nextElementSibling;
      if (!showMoreBtnContainer?.classList.contains('show-more-btn-container')) {
        showMoreBtnContainer = fieldset.parentElement.querySelector('.show-more-btn-container');
      }
      if (!showMoreBtnContainer) return; // no container => can't do show more/less

      let showMoreBtn = showMoreBtnContainer.querySelector('.show-more-btn');
      if (!showMoreBtn) {
        // Create the button if it doesn't exist
        showMoreBtn = document.createElement('button');
        showMoreBtn.classList.add('show-more-btn', 'text-decoration--underline');
        showMoreBtn.textContent = showMoreText;  // Use localized “Show more” text

        // unique ID for localStorage
        showMoreBtn.id = 'show-more-btn-' + showMoreBtnCounter;
        showMoreBtnCounter++;

        showMoreBtnContainer.appendChild(showMoreBtn);
      } else {
        // ensure visible
        showMoreBtn.classList.remove('hidden');
      }

      // Add toggle logic once
      if (!showMoreBtn.dataset.listenerAdded) {
        showMoreBtn.addEventListener('click', () => {
          const isExpanded = showMoreBtn.textContent === showLessText;

          if (isExpanded) {
            // Hide items beyond first X
            let idx = 0;
            enabledVariantLabels.forEach(label => {
              if (idx >= VISIBLE_ITEM_COUNT) {
                label.classList.add('custom-hidden');
              }
              idx++;
            });
            showMoreBtn.textContent = showMoreText;
            setShowMoreState(showMoreBtn.id, false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            // Show all
            enabledVariantLabels.forEach(label => label.classList.remove('custom-hidden'));
            showMoreBtn.textContent = showLessText;
            setShowMoreState(showMoreBtn.id, true);
          }
        });
        showMoreBtn.dataset.listenerAdded = 'true';
      }

      // Apply saved expand/collapse state
      const isExpanded = getShowMoreState(showMoreBtn.id);
      if (isExpanded) {
        enabledVariantLabels.forEach(label => label.classList.remove('custom-hidden'));
        showMoreBtn.textContent = showLessText;
      } else {
        let idx = 0;
        enabledVariantLabels.forEach(label => {
          if (idx >= VISIBLE_ITEM_COUNT) {
            label.classList.add('custom-hidden');
          }
          idx++;
        });
        showMoreBtn.textContent = showMoreText;
      }

    } else {
      // fewer or equal => show all
      variantLabels.forEach(label => label.classList.remove('custom-hidden'));

      // hide any existing "Show more" button
      let showMoreBtnContainer = fieldset.nextElementSibling;
      if (!showMoreBtnContainer?.classList.contains('show-more-btn-container')) {
        showMoreBtnContainer = fieldset.parentElement.querySelector('.show-more-btn-container');
      }
      if (showMoreBtnContainer) {
        const showMoreBtn = showMoreBtnContainer.querySelector('.show-more-btn');
        if (showMoreBtn) {
          showMoreBtn.classList.add('hidden');
        }
      }
    }
  });
}

/**
 * Saves the expanded/collapsed state of a "Show more" button.
 */
function setShowMoreState(buttonId, isExpanded) {
  localStorage.setItem(buttonId, isExpanded ? 'true' : 'false');
}

/**
 * Retrieves the expanded/collapsed state of a "Show more" button.
 */
function getShowMoreState(buttonId) {
  const state = localStorage.getItem(buttonId);
  if (state !== null) {
    return state === 'true';
  }
  return false;
}

/* =======================================================================
   Toggle VAT Functions
======================================================================== */

/**
 * On load, we have everything with VAT by default.
 * If includeVAT=false from cookie, we must convert all (table and price_current) to exVAT by dividing by (1+vatRate).
 */
function initializeVATToggle() {
  const vatCheckbox = document.getElementById('show-vat-checkbox');
  if (!vatCheckbox) {
    return;
  }

  const vatCookie = getCookie('includeVAT');
  if (vatCookie !== null) {
    includeVAT = (vatCookie === 'true');
  } else {
    includeVAT = true;
    setCookie('includeVAT', true, 30);
  }

  vatCheckbox.checked = includeVAT;

  initDiscountBracketsWhenTableIsReady(function() {
    updateDiscountTablePricesWithVAT();
    updatePrice();
  });

  vatCheckbox.addEventListener('change', function() {
    includeVAT = vatCheckbox.checked;
    setCookie('includeVAT', includeVAT, 30);

    updateDiscountTablePricesWithVAT();
    updatePrice();
  });
}

/* =======================================================================
   Price Update Functions
======================================================================== */

/**
 * Updates the discount table prices based on includeVAT flag,
 * calculating each row's final unit price with or without VAT,
 * then forcing 4 decimals and coloring the last 2 in gray.
 */
function updateDiscountTablePricesWithVAT() {
  const table = document.querySelector('.lb-vd-table');
  if (!table) return;

  const rows = table.querySelectorAll('tbody tr');
  rows.forEach(row => {
    const cells = row.querySelectorAll('td');
    if (cells.length < 3) return;

    const discountText = cells[1].textContent.trim(); 
    const discountValue = parseDiscountPercentage(discountText);

    let finalUnitPrice = originalSingleUnitPriceWithVAT * (1 - discountValue / 100);

    if (includeVAT) {
      finalUnitPrice = finalUnitPrice * (1 + vatRate);
    }

    const formattedPrice = formatPrice(finalUnitPrice, 4);

    const priceCell = cells[2];
    const moneySpan = priceCell.querySelector('.money');
    if (moneySpan) {
      moneySpan.textContent = formattedPrice;
    }
  });

  // Finally, color the last two digits in gray
  rewriteQtyPricesToMultipleDecimals();
}

/**
 * Rewrites all <span class="money"> elements in .lb-vd-table to show 4 decimals,
 * coloring the last two digits in gray. It respects your currencySymbolPlacement
 * and decimal separator, assuming the text is already at 4 decimals
 * (e.g. "0.1275£" or "0,1275€").
 */
function rewriteQtyPricesToMultipleDecimals() {
  const table = document.querySelector('.lb-vd-table');
  if (!table) return;

  // Select all .money spans in the table body
  const moneyElements = table.querySelectorAll('tbody tr td .money');
  if (!moneyElements.length) return;

  // Determine whether the symbol is before/after the price and the decimal separator
  const decimalSeparator = determineDecimalSeparator(currencySymbol);

  moneyElements.forEach(moneyEl => {
    let originalText = moneyEl.textContent.trim();

    // 1) Temporarily remove the currency symbol if it's at the start or the end
    let numericText = originalText;
    if (currencySymbolPlacement === 'before') {
      if (numericText.startsWith(currencySymbol)) {
        numericText = numericText.slice(currencySymbol.length).trim();
      }
    } else {
      if (numericText.endsWith(currencySymbol)) {
        numericText = numericText.slice(0, -currencySymbol.length).trim();
      }
    }

    // 2) Find the decimal separator and ensure we have 4 digits.
    //    If your "formatPrice(...,4)" already gives 4 decimals,
    //    just color the last two digits here.
    let decimalIndex = numericText.indexOf(decimalSeparator);
    if (decimalIndex === -1) {
      // If there's no separator, add one plus four zeros
      numericText += decimalSeparator + '0000';
      decimalIndex = numericText.indexOf(decimalSeparator);
    }

    // Split into integer part + separator and fraction
    let integerPart = numericText.slice(0, decimalIndex + 1);
    let fractionPart = numericText.slice(decimalIndex + 1);

    // Make sure the fraction part has at least 4 digits
    fractionPart = fractionPart.padEnd(4, '0');

    // Separate the last 2 digits for coloring
    let mainFraction = fractionPart.slice(0, -2);
    let lastTwo = fractionPart.slice(-2);

    // 3) Build the final number with the last 2 digits in gray
    let numericHtml = integerPart + mainFraction + `<span style="color:#888">${lastTwo}</span>`;

    // 4) Re-insert the currency symbol in the correct position
    let finalText = '';
    if (currencySymbolPlacement === 'before') {
      finalText = currencySymbol + numericHtml;
    } else {
      finalText = numericHtml + currencySymbol;
    }

    // 5) Update the element
    moneyEl.innerHTML = finalText;
  });
}

/**
 * Updates the main product price (.price__current) based on the current quantity,
 * the discount bracket, the VAT setting, and the vatRate.
 */
function updatePrice() {
  // 1) If there's no .price__current element, exit
  const priceCurrent = document.querySelector('.price__current');
  if (!priceCurrent) return;

  // 2) Get current quantity
  const quantity = parseInt(quantityInput?.value || "1");
  if (isNaN(quantity) || quantity < 1) {
    return;
  }

  // 3) Find the discount bracket corresponding to this quantity
  const bracket = getDiscountBracket(quantity);

  // Use base price without VAT by default
  let basePrice = originalSingleUnitPriceWithVAT;

  // Default discountPercentage is 0 if no bracket is found
  let discountPercentage = 0;
  if (bracket) {
    const discountCell = bracket.rowElement.querySelectorAll('td')[1];
    if (discountCell) {
      discountPercentage = parseDiscountPercentage(discountCell.textContent.trim());
    }
  }

  // 4) Apply the discount
  let unitPrice = basePrice * (1 - discountPercentage / 100);

  // 5) If VAT is enabled, add VAT to the price
  if (includeVAT) {
    unitPrice = unitPrice * (1 + vatRate);
  }

  // 6) Calculate total = unitPrice * quantity
  let totalPrice = unitPrice * quantity;

  // 7) Display the total price in .price__current
  priceCurrent.textContent = formatPrice(totalPrice, 2);

  // 8) Update the "was" price if applicable
  const priceWas = document.querySelector('.price__was');
  if (priceWas && originalComparePriceWithVAT > 0) {
    let compareUnitPrice = originalComparePriceWithVAT;
    if (includeVAT) {
      compareUnitPrice = compareUnitPrice * (1 + vatRate);
    }
    let compareTotalPrice = compareUnitPrice * quantity;
    priceWas.textContent = formatPrice(compareTotalPrice, 2);
  }
}

/* =======================================================================
   Metafields Functions
======================================================================== */

/**
 * Fetches the variant metafields from the backend API using the given iso_code.
 * @param {string} variantId - The ID of the variant to fetch metafields for.
 */
function fetchProductVariantMetafieldsApiCall(variantId) {
  return fetch('https://api-shopify.enveseur.com/product/variant/' + variantId + '/lang/' + iso_code)
  .then(response => response.json())
  .catch(error => {
      console.error('Error fetching variant metafields:', error);
  });
}

/**
 * Populates the metafield values in all <td> elements with matching class names.
 * If a metafield is missing or its value is "No value", hides the corresponding <tr>.
 *
 * @param {Object} variant - The variant data containing metafields.
 */
function displayVariantMetafieldsForContainers(variant) {
  // Iterate through the metafields in the variant data
  if (variant.metafields) {
    for (const key in variant.metafields) {
      if (variant.metafields.hasOwnProperty(key)) {
        const metafield = variant.metafields[key];

        // Select all <td> elements with the corresponding class
        const tdElements = document.querySelectorAll(`.product-property-value-${key}`);

        // Check if the metafield has a valid value
        const hasValue = metafield.value && metafield.value !== 'No value' && metafield.value !== 'N';

        if (tdElements.length > 0) {
          tdElements.forEach(tdElement => {
            const parentRow = tdElement.closest('tr');
            if (hasValue) {
              // Populate the element with the metafield value
              var metafield_value = metafield.value;
              if(metafield_value == "Y"){
                metafield_value = '<i class="fa-solid fa-check"></i>';
              }
              
              tdElement.innerHTML = metafield_value;
              
              if (parentRow) {
                parentRow.classList.remove('custom-hidden');
              }
            } else if (parentRow) {
              // Hide the corresponding <tr> if no value
              parentRow.classList.add('custom-hidden');
            }
          });
        }
      }
    }
  }

  // Hide any remaining rows with no matching metafield
  const allRows = document.querySelectorAll('.product-metafields-container tr');
  allRows.forEach(row => {
    const key = row.className.match(/product-property-([^\s]+)/)?.[1];
    if (key && !variant.metafields.hasOwnProperty(key)) {
      row.classList.add('custom-hidden');
    }
  });
}

/**
 * Formats the metafield key to be more readable if no custom title is found.
 * Replaces underscores with spaces and capitalizes words.
 * @param {string} key - The metafield key to format.
 * @returns {string} The formatted key.
 */
function formatMetafieldKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/* =======================================================================
   Cookie Helper Functions
======================================================================== */

/**
 * Sets a cookie with a given name, value, and expiration in days.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to store in the cookie.
 * @param {number} days - The number of days until the cookie expires.
 */
function setCookie(name, value, days) {
  var expires = "";
  if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

/**
 * Gets the value of a cookie by name.
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie or null if not found.
 */
function getCookie(name) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/* =======================================================================
   Price Parsing/Formatting Helper Functions
======================================================================== */

/**
 * Parses a price string (e.g., "0,23 + currencySymbol") into a float number.
 * Assumes the format uses a comma as decimal separator and the currencySymbol at the end.
 * @param {string} priceText - The text containing the price (e.g. "0,23 + currencySymbol").
 * @returns {number} The parsed unit price as a float.
 */
function parseUnitPrice(priceText) {
  // Remove any non-numeric characters except comma and dot, then replace comma with dot
  const cleaned = priceText.replace(/[^0-9.,]/g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Extracts the discount percentage from texts like "- 10%", "-15%", etc.
 * Returns only the numeric portion (10, 15, etc.). If not found, returns 0.
 *
 * @param {string} cellText - The text containing the discount percentage.
 * @returns {number} The parsed discount percentage (e.g., 10), or 0 if none is found.
 */
function parseDiscountPercentage(cellText) {
  // Looks for something like "- 10%", "-15%", "- 5.5%", etc.
  const match = cellText.match(/-\s*(\d+(?:\.\d+)?)%/);
  if (!match) {
    return 0;
  }
  return parseFloat(match[1]) || 0;
}

/**
 * Formats a number into the desired price format with the currency symbol.
 * Respects the configured symbol position (before/after) and the decimal separator.
 *
 * @param {number} price - The numeric value to format.
 * @param {number} [decimals=2] - How many decimal places to include (default is 2).
 * @returns {string} A string representing the formatted price, e.g. "£12.3456" or "12,3456€".
 */
function formatPrice(price, decimals = 2) {
  const decimalSeparator = determineDecimalSeparator(currencySymbol);

  // Force the specified number of decimal places
  let numberPart = price.toFixed(decimals);

  // If the chosen separator is a comma, replace the dot
  if (decimalSeparator === ',') {
    numberPart = numberPart.replace('.', ',');
  }

  // Place the symbol before or after the number
  if (currencySymbolPlacement === 'before') {
    // e.g. "£12.34"
    return currencySymbol + numberPart;
  } else {
    // e.g. "12,34€"
    return numberPart + currencySymbol;
  }
}

/**
 * Determines whether the currency symbol should appear before or after the price.
 * You can customize this mapping according to your specific use case.
 *
 * @param {string} symbol - The detected currency symbol.
 * @returns {'before' | 'after'} - 'before' to place the symbol before the price, 'after' to place it after.
 */
function determineCurrencySymbolPlacement(symbol) {
  // List of symbols that are typically placed before the price
  const symbolsBefore = new Set(['$', '£', '¥']); 
  // List of symbols that are typically placed after the price
  // (In some regions, the euro is used before the price, but here we place it after as per your request)
  const symbolsAfter = new Set(['€']);

  // If the detected symbol is in the "symbolsBefore" list, return 'before'
  if (symbolsBefore.has(symbol)) {
    return 'before';
  }
  // If it is in the "symbolsAfter" list, return 'after'
  if (symbolsAfter.has(symbol)) {
    return 'after';
  }
  // If the symbol is not in either list, assume 'after' as the default
  // or customize the default behavior as needed
  return 'after';
}

/**
 * Determines whether to use a dot (.) or comma (,) as the decimal separator.
 * @param {string} symbol - The detected currency symbol.
 * @returns {string} - Returns the decimal separator: '.' or ','.
 */
function determineDecimalSeparator(symbol) {
  // British Pound (£) uses a dot as the decimal separator
  if (symbol === '£') {
    return '.';
  }
  // Default to comma for other currencies
  return ',';
}

/* =======================================================================
   Utility Functions
======================================================================== */

/**
 * Smoothly scrolls to a target element over a specified duration.
 * @param {Element} target - The element to scroll to.
 * @param {number} duration - The duration of the scroll in milliseconds.
 */
function smoothScrollTo(target, duration) {
  const start = window.pageYOffset;
  const stickyMenu = document.querySelector('.header');
  const stickyMenuHeight = stickyMenu ? stickyMenu.offsetHeight : 0;
  const targetPosition = target.getBoundingClientRect().top + start - stickyMenuHeight;
  const distance = targetPosition - start;
  const startTime = performance.now();

  function scrollAnimation(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    const ease = easeInOutQuad(progress);
    window.scrollTo(0, start + distance * ease);

    if (progress < 1) {
      requestAnimationFrame(scrollAnimation);
    }
  }

  function easeInOutQuad(t) {
    return t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  requestAnimationFrame(scrollAnimation);
}